import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Modal,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 720,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const AlumniFilters = ({ onSearch, filterOptions }) => {
  const [filters, setFilters] = useState({
    search: '',
    province: '',
    category: '',
    course: '',
    graduationYear: '',
    approvalCodeName: '',
  });

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            name="search"
            label="Cerca per nome, competenze, codice..."
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Provincia</InputLabel>
            <Select name="province" value={filters.province} label="Provincia" onChange={handleChange}>
              <MenuItem value=""><em>Tutte</em></MenuItem>
              {filterOptions.provinces.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Categoria</InputLabel>
            <Select name="category" value={filters.category} label="Categoria" onChange={handleChange}>
              <MenuItem value=""><em>Tutte</em></MenuItem>
              {filterOptions.categories.map((cat) => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Corso</InputLabel>
            <Select name="course" value={filters.course} label="Corso" onChange={handleChange}>
              <MenuItem value=""><em>Tutti</em></MenuItem>
              {filterOptions.courses.map((course) => (
                <MenuItem key={course} value={course}>{course}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Anno Fine</InputLabel>
            <Select name="graduationYear" value={filters.graduationYear} label="Anno Fine" onChange={handleChange}>
              <MenuItem value=""><em>Tutti</em></MenuItem>
              {filterOptions.graduationYears.map((year) => (
                <MenuItem key={year} value={String(year)}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Codice Approvazione</InputLabel>
            <Select
              name="approvalCodeName"
              value={filters.approvalCodeName}
              label="Codice Approvazione"
              onChange={handleChange}
            >
              <MenuItem value=""><em>Tutti</em></MenuItem>
              {filterOptions.approvalCodeNames.map((codeName) => (
                <MenuItem key={codeName} value={codeName}>{codeName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Button fullWidth variant="contained" onClick={() => onSearch(filters)} startIcon={<SearchIcon />}>
            Cerca
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const AdminSearchAlumni = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    courses: [],
    approvalCodeNames: [],
    graduationYears: [],
    provinces: [],
  });

  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await axios.get('/api/admin/alumni/filter-options');
        setFilterOptions({
          categories: res.data.categories || [],
          courses: res.data.courses || [],
          approvalCodeNames: res.data.approvalCodeNames || [],
          graduationYears: res.data.graduationYears || [],
          provinces: res.data.provinces || [],
        });
      } catch (err) {
        console.error('Errore caricamento filtri admin alumni:', err);
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        setLoading(true);
        const res = await axios.get('/api/admin/alumni', { params: activeFilters });
        setAlumni(res.data);
      } catch (err) {
        setError('Impossibile caricare la lista alumni per amministrazione.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlumni();
  }, [activeFilters]);

  const handleSearch = (filters) => {
    const newFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '')
    );
    setActiveFilters(newFilters);
  };

  const handleOpenModal = async (alumno) => {
    try {
      setDetailsLoading(true);
      const res = await axios.get(`/api/admin/user-details/${alumno.id}`);
      setSelectedAlumni(res.data);
    } catch (err) {
      console.error('Errore nel recupero dettaglio utente:', err);
      setError('Errore nel recupero del profilo completo.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseModal = () => setSelectedAlumni(null);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Ricerca Alumni (Admin)</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Sezione riservata agli amministratori con filtri avanzati e dettaglio completo profilo.
      </Typography>

      <AlumniFilters onSearch={handleSearch} filterOptions={filterOptions} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Paper>
          <List>
            {alumni.length > 0 ? alumni.map((alunno, index) => (
              <React.Fragment key={alunno.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <Button variant="outlined" size="small" onClick={() => handleOpenModal(alunno)}>
                      Vedi Profilo Completo
                    </Button>
                  }
                >
                  <ListItemText
                    primary={<Typography variant="h6">{alunno.fullName}</Typography>}
                    secondary={
                      <>
                        <Typography component="p" variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          <strong>Provincia:</strong> {alunno.province || 'Non specificata'}
                        </Typography>
                        <Typography component="p" variant="body2" color="text.primary">
                          <strong>Ambito di ricerca:</strong> {alunno.desiredJob || 'Non specificato'}
                        </Typography>
                        <Typography component="p" variant="body2" color="text.primary">
                          <strong>Anno fine percorso:</strong> {alunno.graduationYear || 'N/D'}
                        </Typography>
                        <Typography component="p" variant="body2" color="text.primary">
                          <strong>Codice approvazione:</strong> {alunno.approvalCodeName || 'N/D'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {(alunno.categories || []).map((c) => (
                            <Chip key={c.category.name} label={c.category.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      </>
                    }
                  />
                </ListItem>
                {index < alumni.length - 1 && <Divider component="li" />}
              </React.Fragment>
            )) : (
              <Typography sx={{ p: 2, textAlign: 'center' }}>
                Nessun alunno trovato con i criteri di ricerca attuali.
              </Typography>
            )}
          </List>
        </Paper>
      )}

      <Modal open={!!selectedAlumni} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : selectedAlumni && (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedAlumni.fullName}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Typography variant="body1" sx={{ mb: 1 }}><strong>Email:</strong> {selectedAlumni.email || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Telefono:</strong> {selectedAlumni.phone || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Indirizzo:</strong> {selectedAlumni.address || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Citta:</strong> {selectedAlumni.city || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Provincia:</strong> {selectedAlumni.province || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Data di nascita:</strong> {selectedAlumni.birthDate ? new Date(selectedAlumni.birthDate).toLocaleDateString('it-IT') : 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Luogo di nascita:</strong> {selectedAlumni.birthPlace || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Formazione:</strong> {selectedAlumni.education || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Ambito ricerca:</strong> {selectedAlumni.desiredJob || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>LinkedIn:</strong> {selectedAlumni.linkedin || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Anno fine percorso:</strong> {selectedAlumni.graduationYear || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><strong>Codice approvazione:</strong> {selectedAlumni.approvalCodeName || 'N/D'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>CV:</strong>{' '}
                {selectedAlumni.resume ? (
                  <a href={`/${selectedAlumni.resume}`} target="_blank" rel="noopener noreferrer">Scarica CV</a>
                ) : 'Non caricato'}
              </Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Competenze</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {(selectedAlumni.categories || []).map((c, idx) => (
                  <Chip key={idx} label={c.category.name} color="primary" size="small" />
                ))}
              </Box>

              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Corsi frequentati</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selectedAlumni.courses || []).map((c, idx) => (
                  <Chip key={idx} label={c.course.name} variant="outlined" size="small" />
                ))}
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleCloseModal}>Chiudi</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default AdminSearchAlumni;
